import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_ROOT = API.replace(/\/api$/, "");

/**
 * FileUploader — reusable uploader with previews. Auto-loads existing uploads for {resource_type, resource_id}.
 *
 * Props:
 *  - resourceType: "estimate" | "job" | "customer" | "business" | "invoice"
 *  - resourceId: string
 *  - accept: string (default images + pdf)
 *  - onUploaded / onDeleted: optional callbacks
 *  - title: optional heading
 */
export default function FileUploader({ resourceType, resourceId, accept = "image/*,application/pdf", title = "Photos & files", canEdit = true }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("lf_token") : null;

  const load = async () => {
    if (!resourceId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/uploads`, { params: { resource_type: resourceType, resource_id: resourceId } });
      setFiles(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resourceType, resourceId]);

  const onSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setBusy(true);
    for (const f of selected) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10 MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("resource_type", resourceType);
      fd.append("resource_id", resourceId);
      fd.append("is_public", "false");
      try {
        await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/uploads/${id}`);
      toast.success("Removed");
      setFiles(files.filter((f) => f.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const displayUrl = (f) => {
    // Prefer short-lived signed URL from backend; fall back to session-auth query for older records.
    if (f.signed_url) {
      if (f.signed_url.startsWith("http")) return f.signed_url;
      return `${BACKEND_ROOT}${f.signed_url}`;
    }
    return `${API}/uploads/${f.id}/download?auth=${encodeURIComponent(token || "")}`;
  };
  const isImg = (f) => (f.content_type || "").startsWith("image/");

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden" data-testid={`file-uploader-${resourceType}`}>
      <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
        <div className="font-heading font-bold text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-[#FF5A00]"/> {title}</div>
        {canEdit && (
          <>
            <input ref={inputRef} type="file" accept={accept} multiple onChange={onSelect} className="hidden" data-testid={`file-uploader-input-${resourceType}`}/>
            <Button
              type="button"
              onClick={()=>inputRef.current?.click()}
              disabled={busy || !resourceId}
              size="sm"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
              data-testid={`file-uploader-btn-${resourceType}`}
            >
              {busy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin"/>Uploading…</> : <><Upload className="w-3 h-3 mr-1"/>Upload</>}
            </Button>
          </>
        )}
      </div>
      <div className="p-4">
        {loading ? (
          <div className="text-center text-xs text-zinc-500 py-4">Loading…</div>
        ) : files.length === 0 ? (
          <div className="text-center text-sm text-zinc-400 py-6">No files yet.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {files.map((f) => (
              <div key={f.id} className="relative group border border-zinc-200 rounded-lg overflow-hidden bg-zinc-50 aspect-square" data-testid={`file-item-${f.id}`}>
                {isImg(f) ? (
                  <img src={displayUrl(f)} alt={f.original_filename} className="w-full h-full object-cover"/>
                ) : (
                  <a href={displayUrl(f)} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center text-zinc-600 hover:text-[#FF5A00] p-2">
                    <FileText className="w-8 h-8 mb-1"/>
                    <div className="text-[10px] text-center truncate w-full">{f.original_filename}</div>
                  </a>
                )}
                {canEdit && (
                  <button
                    onClick={()=>remove(f.id)}
                    className="absolute top-1 right-1 p-1 rounded bg-white/90 hover:bg-rose-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`file-remove-${f.id}`}
                  >
                    <X className="w-3 h-3"/>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
